import { Combobox, Icon } from "@simplybusiness/mobius";
import { useSnowplowContext } from "@simplybusiness/services";
import { useRef, useMemo } from "react";
import { useQuestionnaire } from "../../../../../../../features/questionnaire";
import type { QuestionComponentProps } from "../../types";
import { search } from "@simplybusiness/icons";

type StateOption = {
  value: string;
  label: string;
};

export const USStateQuestion = ({
  questionFieldProps,
  options = [],
}: QuestionComponentProps) => {
  const {
    value,
    onChange,
    onBlur,
    questionLabelProps,
    helpInfo,
    ...otherProps
  } = questionFieldProps;

  const { snowplow } = useSnowplowContext();
  const state = useQuestionnaire();

  const stateOptions = options as StateOption[];

  const hasFiredUsStateClicked = useRef(false);
  const handleUsStateFocus = () => {
    if (!hasFiredUsStateClicked.current) {
      hasFiredUsStateClicked.current = true;
      snowplow?.trigger("usStateClicked");
    }
  };

  // Memoize computed values to avoid recalculating on every render
  const { labels, labelToValueMap, valueToLabelMap } = useMemo(() => {
    // Extract labels for Combobox display
    const labelsList = stateOptions.map(option => option.label);

    // Create mapping from label to value
    const labelToValue = stateOptions.reduce(
      (acc, option) => {
        acc[option.label] = option.value;
        return acc;
      },
      {} as Record<string, string>,
    );

    // Create mapping from value to label for reverse lookup
    const valueToLabel = stateOptions.reduce(
      (acc, option) => {
        acc[option.value] = option.label;
        return acc;
      },
      {} as Record<string, string>,
    );

    return {
      labels: labelsList,
      labelToValueMap: labelToValue,
      valueToLabelMap: valueToLabel,
    };
  }, [stateOptions]);

  const handleSelected = (selectedLabel: string) => {
    const mappedValue = labelToValueMap[selectedLabel] || selectedLabel;

    snowplow?.trigger("stateQuestionAnswered", {
      answer: mappedValue,
      selectedCoverages: state.config?.selectedCoverages,
      selectedType: "coverage_selection",
    });

    onChange?.({
      target: {
        name: questionFieldProps.name,
        value: mappedValue,
      },
    });
  };

  // Convert the incoming value to its corresponding label for display
  const displayValue = value ? valueToLabelMap[value as string] || value : "";

  return (
    <Combobox
      {...otherProps}
      icon={<Icon icon={search} />}
      value={displayValue as string}
      options={labels}
      onSelected={handleSelected}
      onFocus={handleUsStateFocus}
    />
  );
};
